import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import MobileDatePicker from '@mui/lab/MobileDatePicker';
import { ClickAwayListener } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputBox from './input';
import { InputProps } from './input.interface';

const Input = ({
  inputField: { type, value, placeholder, name, label, iconEnd, iconStart },
  onChange,
  onSelect,
  dropdownItems = [],
  readonly = false,
  disabled,
  dir,
}: InputProps) => {
  const [selectedDate, setSelectedDate] = useState<string | null>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const { i18n } = useTranslation();
  const isLTR = i18n.dir() === 'ltr';

  const TextInput = (): JSX.Element => (
    <div className="input">
      {iconStart && <div className={`icon-start row-center  ${!isLTR && 'isRTL'}`}>{iconStart}</div>}
      <input
        style={{ direction: dir }}
        className={`p-sm fs-sm ${iconEnd ? 'pr-md' : ''} ${iconStart ? 'pl-md' : ''}`}
        type="text"
        onChange={(e) => onChange(e.target.value, name)}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );

  const NumberInput = (): JSX.Element => (
    <div className="input">
      <input
        className={`p-sm fs-sm ${iconEnd ? 'pr-md' : ''} ${iconStart ? 'pl-md' : ''}`}
        type="number"
        onChange={(e) => onChange(e.target.value, name)}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        style={{ direction: dir }}
      />
    </div>
  );

  const TextareaInput = (): JSX.Element => <div className="input"></div>;

  const PasswordInput = (): JSX.Element => (
    <div className="input">
      <input
        className={`p-sm fs-sm ${iconEnd ? 'pr-md' : ''} ${iconStart ? 'pl-md' : ''}`}
        type="password"
        onChange={(e) => onChange(e.target.value, name)}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        style={{ direction: dir }}
      />
    </div>
  );

  const DatePcikerInput = (): JSX.Element => (
    <div className="input">
      {iconStart && <div className={`icon-start row-center  ${!isLTR && 'isRTL'}`}>{iconStart}</div>}
      <LocalizationProvider dateAdapter={DateAdapter}>
        <MobileDatePicker
          label={label}
          value={value}
          onAccept={(): void => {
            onChange(selectedDate, name);
          }}
          onChange={(newValue): void => {
            setSelectedDate(newValue);
          }}
          renderInput={({ inputRef, inputProps, InputProps }) => (
            <input
              className={`p-sm fs-sm ${iconEnd ? 'pr-md' : ''} ${iconStart ? 'pl-md' : ''}`}
              ref={inputRef}
              placeholder="DD/MM/YYYY"
              {...inputProps}
              disabled={disabled}
            />
          )}
        />
      </LocalizationProvider>
    </div>
  );

  const SelectInput = (): JSX.Element => (
    <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
      <div className="input">
        <input
          className={`p-sm fs-sm ${iconEnd ? 'pr-md' : ''} ${iconStart ? 'pl-md' : ''}`}
          type="text"
          readOnly={true}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={() => { }}
          onClick={() => {
            if (disabled) return;
            setShowDropdown(true);
          }}
        />

        <div className={`dropdown px-sm ${showDropdown ? 'active' : ''}`}>
          {dropdownItems.map(
            (item, index): JSX.Element => (
              <div
                className="dropdown__item py-sm"
                key={index}
                onMouseDownCapture={(e) => {
                  setShowDropdown(false);
                  onSelect!(item.value, item.name, name);
                }}
              >
                {item.name}
              </div>
            )
          )}
        </div>
        <div className={`icon-end row-center ${!isLTR && 'isRTL'}`}>
          <ArrowDropDownIcon />
        </div>
      </div>
    </ClickAwayListener>
  );

  return (
    <InputBox>
      {label && <div className="input-box__label fs-sm mb-xxs">{label}</div>}

      {type === 'text' && TextInput()}
      {type === 'number' && NumberInput()}
      {type === 'textarea' && TextareaInput()}
      {type === 'password' && PasswordInput()}
      {type === 'datepicker' && DatePcikerInput()}
      {type === 'select' && SelectInput()}
    </InputBox>
  );
};

export default Input;
